import BestPlans from '../assets/retailHealthInsurance/best-plans.webp';
import HospitalRoom from '../assets/retailHealthInsurance/hospital-room.webp';
import MedicalSupplies from '../assets/retailHealthInsurance/medical-supplies.webp';
import KeepsGrowing from '../assets/retailHealthInsurance/keeps-growing.webp';
import RestoresItself from '../assets/retailHealthInsurance/restore-itself.webp';
import { z } from 'zod';
import { EAddMemberRelationship, IHealthInsuranceConfig, TMemberGender } from '@/types';
import { calculateAge } from '@/utils/calculateAge';
import { MAX_MEMBER_AGE_YEARS } from '@/utils/memberDob';
import { differenceInDays } from 'date-fns';

export const PLANS_BENEFITS_SCREENS = [
  {
    title: 'We only show the best plans',
    subtitle: "From India's best insurers and tailored to your needs",
    description:
      'With 1L+ claims processed each year, we know which features truly make a difference',
    button: 'Explore features',
    buttonType: 'tertiary',
    icon: BestPlans,
  },
  {
    tag: 'pick any',
    title: 'Hospital room',
    subtitle:
      'Many plans limit room choice, which impacts claim settlement',
    description:
      'Our plans let you pick any hospital room at the time of hospitalisation',
    icon: HospitalRoom,
    button: 'Skip to show all plans',
    buttonType: 'tertiary',
  },
  {
    tag: 'cover',
    title: 'Medical supplies',
    subtitle:
      'Most plans do not cover the cost of basic supplies like masks, syringes, and surgical items',
    description:
      'Our plans cover medical supplies thus enhancing your claims payout',
    icon: MedicalSupplies,
    button: 'Skip to show all plans',
    buttonType: 'tertiary',
  },
  {
    tag: 'cover that',
    title: 'Keeps growing',
    subtitle:
      'Many plans offer static cover, leaving you underinsured as costs rise',
    description: 'Our plans offer at least 50% increase in coverage annually',
    icon: KeepsGrowing,
    button: 'Skip to show all plans',
    buttonType: 'tertiary',
  },
  {
    tag: 'cover that',
    title: 'Restores itself',
    subtitle: "Most plans don't reset, leaving you vulnerable to future claims",
    description:
      "Our plans restore coverage after each claim, so you're always protected",
    button: 'Show Plans',
    buttonType: 'primary',
    icon: RestoresItself,
  },
];

export const BEST_COVERAGE_BULLET_POINTS = [
  {
    title: 'Choose any hospital room',
    subtitle: 'No restrictions at the time of hospitalisation',
  },
  {
    title: 'No co-pay, no hidden charges',
    subtitle:
      'What you see is what you get. No surprises at the time of claim settlement',
  },
  {
    title: 'Cover cost of medical supplies',
    subtitle:
      'Gloves, syringes, masks, surgical supplies—all covered to cut your out-of-pocket costs',
  },
];

export const HASSLE_FREE_BULLET_POINTS = [
  {
    title: 'Your claims, our priority',
    subtitle:
      '10,000+ claims processed monthly, 85% settled within a week. You’re in good hands',
  },
  {
    title: 'Dedicated claims buddy',
    subtitle:
      'A claims expert handles everything for you, from paperwork to approvals',
  },
  {
    title: 'We fight for fair settlements',
    subtitle:
      'Every month, we recover ₹25L+ from disputed claims for our users. Trust us to get you what’s rightfully yours',
  },
];

export const MORE_COVERAGE_BULLET_POINTS = [
  {
    title: 'Adapt to rising medical costs',
    subtitle:
      'Enjoy at least a 50% coverage increase annually, ensuring long-term protection',
  },
  {
    title: 'Tailor your cover each year',
    subtitle:
      'Adjust your cover to cover more members, increase coverage, or add features',
  },
  {
    title: 'Free Cover Evaluation & Upgrade',
    subtitle:
      'Annual assessments with a Plum expert, plus the option to upgrade your coverage with waived waiting periods!',
  },
];

export const BEST_PLUM_BULLET_POINTS = [
  {
    title: 'Lifetime access',
    subtitle:
      'Continue using Plum app and services, regardless of your employment status',
  },
  {
    title: 'On-demand claims & advisory support',
    subtitle:
      'Reach out to us for any queries on existing policies or to get additional coverage',
  },
  {
    title: 'Beyond hospital bills',
    subtitle:
      'Access doctor consultations and health checkups on the Plum app, for life',
  },
];

export const RHI_WHATSAPP_INITIAL_MESSAGE =
  `Hi Saypotree, I'd like to explore personal health insurance plans with Plum`;
export const RHI_PHONE_NUMBER = '9980469489';

export const RHI_USER_QUERY_MESSAGE = 'wanting to buy health insurance';

export const GENDER_OPTIONS: { label: string; value: TMemberGender }[] = [
  { label: 'Male', value: 'MALE' },
  { label: 'Female', value: 'FEMALE' },
];

/** When self and spouse are both insured, the spouse is the other gender: insurers refuse a same-gender spouse at proposal submit. */
export const oppositeGender = (gender: TMemberGender): TMemberGender =>
  gender === 'MALE' ? 'FEMALE' : 'MALE';

const memberValueSchema = z.object({
  isChecked: z.boolean(),
  dob: z.string(),
  name: z.string().optional(),
  gender: z.enum(['MALE', 'FEMALE']).optional(),
});

type TMemberValue = z.infer<typeof memberValueSchema>;

const validateMember = (
  ctx: z.RefinementCtx,
  member: TMemberValue,
  path: (string | number)[],
  band: 'adult' | 'child',
  label: string,
  config: IHealthInsuranceConfig,
) => {
  if (!member.isChecked) return;

  const addIssue = (message: string, field: 'dob' | 'gender') =>
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: [...path, field],
    });

  if (!member.gender) {
    addIssue(`Gender is mandatory for ${label}`, 'gender');
  }

  if (!member.dob?.trim()) {
    addIssue(`Date of birth is mandatory for ${label}`, 'dob');
    return;
  }

  const dob = new Date(member.dob);
  if (Number.isNaN(dob.getTime())) {
    addIssue('Please enter a valid date of birth', 'dob');
    return;
  }

  if (dob.getTime() > Date.now()) {
    addIssue('Date of birth cannot be in the future', 'dob');
    return;
  }

  const age = calculateAge(member.dob);

  if (band === 'child') {
    if (age < config.ageConstraints.child.min.value) {
      addIssue(`Age must be at least ${config.ageConstraints.child.min.value}`, 'dob');
    } else if (age >= config.ageConstraints.child.max.value) {
      addIssue(`Age must be less than ${config.ageConstraints.child.max.value}`, 'dob');
    }
    return;
  }

  if (age < config.ageConstraints.adult.min.value) {
    addIssue(`Age must be at least ${config.ageConstraints.adult.min.value}`, 'dob');
  } else if (age > MAX_MEMBER_AGE_YEARS) {
    addIssue(`Age must be less than or equal to ${MAX_MEMBER_AGE_YEARS}`, 'dob');
  }
};

export const createMemberSchema = (config: IHealthInsuranceConfig) =>
  z
    .object({
      self: memberValueSchema,
      spouse: memberValueSchema,
      children: z.array(memberValueSchema),
      parents: z.array(memberValueSchema),
      pincode: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (!config) return;

      validateMember(ctx, data.self, ['self'], 'adult', 'self', config);
      validateMember(ctx, data.spouse, ['spouse'], 'adult', 'spouse', config);

      if (
        data.self.isChecked &&
        data.spouse.isChecked &&
        data.self.gender &&
        data.spouse.gender === data.self.gender
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Spouse's gender must be different from yours",
          path: ['spouse', 'gender'],
        });
      }

      data.children.forEach((child, index) => {
        validateMember(ctx, child, ['children', index], 'child', 'child', config);
      });

      data.parents.forEach((parent, index) => {
        validateMember(ctx, parent, ['parents', index], 'adult', 'parent', config);
      });
    });

export const FAQ = [
  {
    question:
      'Why should I buy personal health insurance if I already have corporate coverage?',
    answer: `Corporate insurance ends when you switch jobs and doesn't grow with your needs or inflation. Personal health insurance stays with you, offering long-term, flexible coverage that adapts to your changing requirements.`,
  },
  {
    question: 'Can I switch to Plum if I already have health insurance?',
    answer:
      'Yes! Plum offers a free evaluation of your current policy and guides you to the best plan. We help you port your policy, maintaining continuity benefits like waiting period waivers.',
  },
  {
    question: 'What is the best time to buy personal health insurance?',
    answer: `The best time is when you're healthy and young. Premiums are lower, and you're more likely to be approved. Waiting could lead to higher costs or rejection.`,
  },
  {
    question: 'How much does personal health insurance cost?',
    answer: `Premiums start at approximately ₹1,000/month for ₹5 lakh coverage, depending on age, medical history, and location. Costs rise with age or health issues, but it's an essential investment.`,
  },
  {
    question: 'How soon can I make a claim after purchasing the insurance?',
    answer:
      'Emergency claims can be made after 31 days. For pre-existing conditions, there is usually a waiting period of 2-4 years, depending on the specific plan. Accidents are covered immediately from day 1.',
  },
];

export const TESTIMONIALS = [
  {
    title: 'They are genuinely invested in helping you',
    subTitle:
      'They assisted me with both medical and life insurance, and they were very patient in explaining the details and benefits. It feels like they are genuinely invested in helping you make informed decisions.',
    userName: 'Kawaljeet Singh',
    userIcon: '',
    userAge: '33 years',
    insurer: 'HDFC Ergo',
    planType: 'Optima Secure',
  },
  {
    title: `I've had an incredible experience with Plum Personal`,
    subTitle: `As someone who values both affordability and comprehensive care, I've had an incredible experience with Plum Personal. The process was straightforward and the team has been nothing short of helpful, clarifying every query patiently.`,
    userName: 'Madri Mohan Prasad',
    userIcon: '',
    userAge: '38 years',
    insurer: 'ICICI Lombard',
    planType: 'Elevate',
  },
  {
    title: 'The process revolved around consultative approach',
    subTitle: `The process revolved around consultative approach and curating a plan which was more suitable for me. Krishna was super helpful during the process`,
    userName: 'Alan Fernandes',
    userIcon: '',
    userAge: '38 years',
    insurer: 'ICICI Lombard',
    planType: 'Elevate',
  },
  {
    title: 'In an industry filled with jargons, the process was quite simple',
    subTitle: `In an industry filled with jargons, the process was quite simple. Plum  was super helpful during the entire journey`,
    userName: 'Saurabh Moody',
    userIcon: '',
    userAge: '41 years',
    insurer: 'HDFC Ergo',
    planType: 'Optima Secure',
  },
];

export const questionAnswerSchema = z.object({
  questionId: z.string(),
  answer: z.union([z.string(), z.array(z.string()), z.number()]),
  otherAnswer: z.string().optional(),
});

export const memberFormSchema = z.object({
  id: z.string(),
  name: z.string().min(5, "Name must be at least 5 characters"),
  gender: z.string().min(1, "Gender is required"),
  dateOfBirth: z.string().min(1, "Date of birth is required"),
  heightFt: z
    .union([z.number(), z.string()])
    .transform((val) => {
      if (typeof val === "string") {
        // Check if string contains only numbers and no leading zeros (except for "0")
        if (!/^(?!0\d)\d+$/.test(val)) {
          return undefined;
        }
        const num = parseFloat(val);
        return Number.isNaN(num) ? undefined : Math.floor(num);
      }
      return val;
    })
    .refine((val) => val !== undefined && val !== null && val >= 1, {
      message: "Height (feet) is required",
    })
    .refine((val) => val !== undefined && val <= 8, {
      message: "Height (feet) cannot be more than 8",
    }),
  heightInches: z
    .union([z.number(), z.string()])
    .transform((val) => {
      if (typeof val === "string") {
        const num = parseFloat(val);
        return Number.isNaN(num) ? undefined : Math.floor(num);
      }
      return val;
    })
    .refine((val) => val !== undefined && val !== null, {
      message: "Height (inches) is required",
    })
    .refine((val) => val !== undefined && val >= 0, {
      message: "Height (inches) cannot be negative",
    })
    .refine((val) => val !== undefined && val <= 11, {
      message: "Height (inches) cannot be more than 11",
    }),
  weightKg: z
    .union([z.number(), z.string()])
    .transform((val) => {
      if (typeof val === "string") {
        // Check if string contains only numbers and at most one decimal point, no leading zeros
        if (!/^(?!0\d)\d+(\.\d{0,1})?$/.test(val)) {
          return undefined;
        }
        const num = parseFloat(val);
        return Number.isNaN(num) ? undefined : parseFloat(num.toFixed(1));
      }
      return val;
    })
    .refine((val) => val !== undefined && val !== null && val > 0, {
      message: "Weight is required",
    }),
  answers: z.array(questionAnswerSchema),
  relation: z.string(),
}).superRefine((data, ctx) => {
  const age = calculateAge(data.dateOfBirth);
  const ageInDays = differenceInDays(new Date(), new Date(data.dateOfBirth));

  if (data.relation.includes('CHILD')) {
    if (ageInDays < 91) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Child must be at least 91 days old",
        path: ["dateOfBirth"],
      });
    } else if (age >= 30) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Child must be under 30 years old",
        path: ["dateOfBirth"],
      });
    }
  } else {
    if (age < 18) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Must be at least 18 years old",
        path: ["dateOfBirth"],
      });
    }
  }
});

export const proposerSchema = z
  .object({
    name: z.string().min(1, "Proposer name is required"),
    dateOfBirth: z
      .string()
      .min(1, "Proposer date of birth is required")
      .refine((dob) => calculateAge(dob) >= 18, {
        message: "Proposer must be at least 18 years old",
      }),
    email: z.string().trim().email("Please enter a valid email address"),
    gender: z.string().min(1, "Proposer gender is required"),
    phoneNumber: z
      .string()
      .min(10, "Phone number must be exactly 10 digits")
      .max(10, "Phone number must be exactly 10 digits")
      .regex(/^\d{10}$/, "Phone number must contain only 10 digits"),
    aadharNumber: z.string().optional(),
    panNumber: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const hasAadhar = !!data.aadharNumber?.trim();
    const hasPan = !!data.panNumber?.trim();

    if (!hasAadhar && !hasPan) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Aadhar number is required",
        path: ["aadharNumber"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Aadhar number is required",
        path: ["panNumber"],
      });
      return;
    }

    if (hasAadhar) {
      const aadharRegex = /^\d{12}$/;
      if (!aadharRegex.test(data.aadharNumber!.trim())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Aadhar number must contain exactly 12 digits",
          path: ["aadharNumber"],
        });
      }
    }

    if (hasPan) {
      const panRegex = /^[A-Z]{5}\d{4}[A-Z]$/;
      const normalizedPan = data.panNumber!.trim().toUpperCase();
      if (!panRegex.test(normalizedPan)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please enter a valid PAN number",
          path: ["panNumber"],
        });
      }
    }
  });

export const nomineeSchema = z.object({
  name: z.string().min(1, "Nominee name is required"),
  dateOfBirth: z
    .string()
    .min(1, "Nominee date of birth is required")
    .refine((dob) => new Date(dob) <= new Date(), {
      message: "Nominee date of birth cannot be in the future",
    })
    .refine((dob) => calculateAge(dob) >= 18, {
      message: "Nominee must be at least 18 years old",
    })
    .refine((dob) => calculateAge(dob) <= 74, {
      message: "Nominee must be at most 74 years old",
    }),
  relationship: z.enum(
    [
      EAddMemberRelationship.Son,
      EAddMemberRelationship.Daughter,
      EAddMemberRelationship.Father,
      EAddMemberRelationship.Mother,
      EAddMemberRelationship.Spouse,
    ],
    {
      errorMap: () => ({ message: "Please select a relationship" }),
    },
  ),
});

export const proposalFormSchema = z.object({
  members: z.array(memberFormSchema),
});

export const bankDetailsSchema = z
  .object({
    nameAsInBankAccount: z
      .string()
      .min(1, "Name as in bank account is required"),
    bankName: z.string().min(1, "Bank name is required"),
    bankAccountNumber: z
      .string()
      .min(9, "Account number must be at least 9 digits")
      .max(18, "Account number must be at most 18 digits")
      .regex(/^\d+$/, "Account number must contain only digits"),
    confirmBankAccountNumber: z.string(),
    ifscCode: z
      .string()
      .min(11, "IFSC code must be 11 characters")
      .max(11, "IFSC code must be 11 characters")
      .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/i, "Please enter a valid IFSC code"),
  })
  .superRefine((data, ctx) => {
    if (data.bankAccountNumber.trim() !== data.confirmBankAccountNumber.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Account numbers do not match",
        path: ["confirmBankAccountNumber"],
      });
    }
  });


export const RHI_V2_QUOTE_DEFAULTS = {
  sumInsured: 1000000,
  tenure: 1,
  coverageType: "INDIVIDUAL",
} as const;

/** A plan covers at most this many adults: self, spouse and parents together. */
export const RHI_V2_MAX_ADULTS = 2;

export const RHI_V2_ADULT_LIMIT_COPY = {
  /** For ticking an adult past the limit, or Proceed with too many adults ticked. */
  toast: `A plan covers up to ${RHI_V2_MAX_ADULTS} adults. Untick one to add someone else.`,
};

export const RHI_V2_RELATION = {
  SELF: "self",
  SPOUSE: "spouse",
  SON: "son",
  DAUGHTER: "daughter",
  FATHER: "father",
  MOTHER: "mother",
} as const;

export const RHI_V2_GENDER = {
  MALE: "Male",
  FEMALE: "Female",
} as const;

export const RHI_V2_MAX_VISIBLE_FEATURES = 4;

export const RHI_V2_TENURE_OPTIONS = [1, 2, 3];

/** Shown first in the cover picker: 5L to 1Cr. */
export const RHI_V2_COVER_OPTIONS = [
  500000, 1000000, 1500000, 2000000, 2500000, 5000000, 10000000,
];

/** Revealed by "Show more": the covers above 1Cr. */
export const RHI_V2_MORE_COVER_OPTIONS = [15000000, 20000000, 30000000, 60000000];

export const RHI_V2_ERROR_CODES = {
  PRICE_CHANGED: 'RHI_V2_PRICE_CHANGED',
  PLAN_NOT_COMMITTED: 'RHI_V2_PLAN_NOT_COMMITTED',
  PLAN_NOT_SELECTABLE: 'RHI_V2_PLAN_NOT_SELECTABLE',
  PLAN_ID_REPLACED: 'RHI_V2_PLAN_ID_REPLACED',
  PLAN_LIST_UPDATING: 'RHI_V2_PLAN_LIST_UPDATING',
  PLAN_LIST_CHANGED: 'RHI_V2_PLAN_LIST_CHANGED',
  PLAN_NOT_IN_QUOTE: 'RHI_V2_PLAN_NOT_IN_QUOTE',
  PLAN_NO_LONGER_OFFERED: 'RHI_V2_PLAN_NO_LONGER_OFFERED',
  INSURER_PRICE_FAILED: 'RHI_V2_INSURER_PRICE_FAILED',
  ADDON_OPTION_REQUIRED: 'RHI_V2_ADDON_OPTION_REQUIRED',
  ADDON_OPTION_INVALID: 'RHI_V2_ADDON_OPTION_INVALID',
  ADDON_COMBINATION_NOT_ALLOWED: 'RHI_V2_ADDON_COMBINATION_NOT_ALLOWED',
  ADDON_NOT_APPLIED: 'RHI_V2_ADDON_NOT_APPLIED',
  KYC_REQUIRED: 'RHI_V2_KYC_REQUIRED',
  COMPOSITION_CHANGED: 'RHI_V2_PROPOSAL_COMPOSITION_CHANGED',
  FORM_UNAVAILABLE: 'RHI_V2_FORM_UNAVAILABLE',
  PROPOSAL_NOT_FOUND: 'RHI_V2_PROPOSAL_NOT_FOUND',
  VERSION_CONFLICT: 'RHI_V2_PROPOSAL_VERSION_CONFLICT',
  FORM_VERSION_CHANGED: 'RHI_V2_FORM_VERSION_CHANGED',
  PROPOSAL_FROZEN: 'RHI_V2_PROPOSAL_FROZEN',
  ANSWER_INVALID: 'RHI_V2_ANSWER_INVALID',
  AFFIRMATION_NOT_PERMITTED: 'RHI_V2_AFFIRMATION_NOT_PERMITTED',
  AFFIRMATION_STALE: 'RHI_V2_AFFIRMATION_STALE',
  PROPOSAL_INCOMPLETE: 'RHI_V2_PROPOSAL_INCOMPLETE',
  PROPOSAL_BLOCKED: 'RHI_V2_PROPOSAL_BLOCKED',
  PROPOSAL_NOT_AFFIRMED: 'RHI_V2_PROPOSAL_NOT_AFFIRMED',
  PROPOSAL_PAYMENT_IN_PROGRESS: 'RHI_V2_PROPOSAL_PAYMENT_IN_PROGRESS',
  SUBMIT_IN_PROGRESS: 'RHI_V2_PROPOSAL_SUBMIT_IN_PROGRESS',
  PROPOSAL_REJECTED: 'RHI_V2_PROPOSAL_REJECTED',
  PROPOSAL_UNCONFIRMED: 'RHI_V2_PROPOSAL_UNCONFIRMED',
  D2C_PROFILE_REQUIRED: 'RHI_V2_D2C_PROFILE_REQUIRED',
  QUOTE_SESSION_FORBIDDEN: 'RHI_V2_QUOTE_SESSION_FORBIDDEN',
  INVALID_QUOTE_REQUEST: 'RHI_V2_INVALID_QUOTE_REQUEST',
  QUOTE_SESSION_NOT_FOUND: 'RHI_V2_QUOTE_SESSION_NOT_FOUND',
  QUOTE_NOT_RECALCULABLE: 'RHI_V2_QUOTE_NOT_RECALCULABLE',
  QUOTE_LOCKED: 'RHI_V2_QUOTE_LOCKED',
  FANOUT_BUDGET_EXCEEDED: 'RHI_V2_FANOUT_BUDGET_EXCEEDED',
  PAYMENT_ATTEMPT_EXISTS: 'RHI_V2_PAYMENT_ATTEMPT_EXISTS',
  QUOTE_EXPIRED: 'RHI_V2_QUOTE_EXPIRED',
  PURCHASE_IN_PROGRESS: 'RHI_V2_PURCHASE_IN_PROGRESS',
  PAYMENT_PRECHECK_UNAVAILABLE: 'RHI_V2_PAYMENT_PRECHECK_UNAVAILABLE',
  PAYMENT_LINK_UNAVAILABLE: 'RHI_V2_PAYMENT_LINK_UNAVAILABLE',
  RISKCOVRY_REQUEST_FAILED: 'RHI_V2_RISKCOVRY_REQUEST_FAILED',
  POLICY_DOCUMENT_NOT_READY: 'RHI_V2_POLICY_DOCUMENT_NOT_READY',
  PROPOSAL_NOT_SUBMITTED: 'RHI_V2_PROPOSAL_NOT_SUBMITTED',
  PROPOSAL_AMENDED: 'RHI_V2_PROPOSAL_AMENDED',
  PLAN_SUPERSEDED: 'RHI_V2_PLAN_SUPERSEDED',
  PRICE_UNAVAILABLE: 'RHI_V2_PRICE_UNAVAILABLE',
  KYC_PLAN_NOT_COMMITTED: 'RHI_V2_KYC_PLAN_NOT_COMMITTED',
  KYC_NOT_AVAILABLE: 'RHI_V2_KYC_NOT_AVAILABLE',
  KYC_MISCONFIGURED: 'RHI_V2_KYC_MISCONFIGURED',
  KYC_UPSTREAM_REJECTED: 'RHI_V2_KYC_UPSTREAM_REJECTED',
  KYC_CONSENT_REQUIRED: 'RHI_V2_KYC_CONSENT_REQUIRED',
  KYC_UPSTREAM_UNAVAILABLE: 'RHI_V2_KYC_UPSTREAM_UNAVAILABLE',
  KYC_ALREADY_VERIFIED: 'RHI_V2_KYC_ALREADY_VERIFIED',
  KYC_OTP_INVALID: 'RHI_V2_KYC_OTP_INVALID',
  KYC_OTP_REQUIRED: 'RHI_V2_KYC_OTP_REQUIRED',
  KYC_NOT_VERIFIED: 'RHI_V2_KYC_NOT_VERIFIED',
  KYC_PLAN_NO_LONGER_AVAILABLE: 'RHI_V2_KYC_PLAN_NO_LONGER_AVAILABLE',
  KYC_CARRY_FORWARD_UNCONFIRMED: 'RHI_V2_KYC_CARRY_FORWARD_UNCONFIRMED',
  DOCUMENT_INVALID: 'RHI_V2_DOCUMENT_INVALID',
  DOCUMENT_REJECTED: 'RHI_V2_DOCUMENT_REJECTED',
  DOCUMENT_QUESTION_INVALID: 'RHI_V2_DOCUMENT_QUESTION_INVALID',
  DOCUMENT_STALE: 'RHI_V2_DOCUMENT_STALE',
  PROPOSAL_REVALIDATION_FAILED: 'RHI_V2_PROPOSAL_REVALIDATION_FAILED',
  KYC_IDENTITY_MISMATCH: 'RHI_V2_KYC_IDENTITY_MISMATCH',
  RBAC_ACCESS_DENIED: 'RBAC_ACCESS_DENIED',
} as const;

/** Where the journey goes after a plan is committed; the only KYC field to route on. */
export const RHI_V2_KYC_NEXT_STEP = {
  PROPOSAL: 'PROPOSAL',
  KYC_START: 'KYC_START',
  KYC_RETRY: 'KYC_RETRY',
  KYC_OTP: 'KYC_OTP',
  KYC_POLL: 'KYC_POLL',
  KYC_ACCEPT_PRICE: 'KYC_ACCEPT_PRICE',
} as const;

export const RHI_V2_KYC_STATE = {
  NOT_STARTED: 'NOT_STARTED',
  OTP_REQUIRED: 'OTP_REQUIRED',
  PENDING: 'PENDING',
  VERIFIED: 'VERIFIED',
  FAILED: 'FAILED',
} as const;

export const RHI_V2_KYC_VALUE_TYPE_WIDGET: Record<string, string> = {
  pan: 'PAN',
  text: 'TEXT',
  date: 'DATE',
  phone_number: 'PHONE',
  email: 'EMAIL',
  radio: 'RADIO',
};

export const RHI_V2_KYC_OTP_LENGTH = 6;
export const RHI_V2_KYC_RESEND_COOLDOWN_SECONDS = 30;
export const RHI_V2_KYC_MAX_RESEND_COUNT = 3;

// Why a plan is missing from the quote. Shown to agents only.
export const RHI_V2_UNAVAILABLE_REASONS: Record<
  string,
  { label: string; body: string }
> = {
  INELIGIBLE: {
    label: 'Ineligible',
    body: "Members' ages or details don't fit this plan",
  },
  NOT_OFFERED: {
    label: 'Not offered',
    body: 'Not sold for this cover, tenure or city',
  },
  TEMPORARILY_UNAVAILABLE: {
    label: 'No response',
    body: "Insurer didn't respond. Change a filter to try again",
  },
  // The whole insurer, with no product code.
  NO_RESPONSE: {
    label: 'No response',
    body: "Insurer didn't respond for any plan. Change a filter to try again",
  },
};
export const RHI_V2_UNAVAILABLE_REASON_FALLBACK = {
  label: 'Unavailable',
  body: 'Not available for this quote',
};

// What each kind of conflicting purchase means, in the guide's suggested words.
export const RHI_V2_PURCHASE_CONFLICT_COPY: Record<string, string> = {
  PAYMENT_IN_FLIGHT:
    "A payment you started is still being confirmed. If money left your account, don't pay again.",
  PAID_NOT_ISSUED: "You've already paid for this. Your policy is on its way.",
  POLICY_ISSUING: "You've already paid for this. Your policy is being issued.",
  ACTIVE_SAME_PEOPLE: 'These members already have an active policy. Buy another?',
};

export const CARD_SHADOW = { boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.05)' };
